class FacetFiltersForm extends HTMLElement {
  /* ciw-native-sort-flow */
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    const debounceMs = FacetFiltersForm.isCiwCollection() ? 200 : 800;
    this.debouncedOnSubmit = debounce((event) => {
      if (event.target.closest('[data-ciw-contacts-brand-input], [data-ciw-contacts-lens-type-input], [data-ciw-eg-group-input], [data-ciw-gusa-option-input], [data-ciw-gusa-measurement-min], [data-ciw-gusa-measurement-max], [data-ciw-gusa-client-filter]')) {
        return;
      }
      this.onSubmitHandler(event);
    }, debounceMs);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static isCiwCollection() {
    const productGrid = document.getElementById('product-grid');
    return Boolean(productGrid && productGrid.classList.contains('ciw-product-grid'));
  }

  static isNativeEyeglassesSort() {
    const params = new URLSearchParams(
      FacetFiltersForm.activeSearchParams || window.location.search.slice(1)
    );
    const sort = params.get('sort_by');
    return (
      sort === 'price-ascending' ||
      sort === 'price-descending' ||
      sort === 'title-ascending' ||
      sort === 'title-descending'
    );
  }

  static shouldRunShopAllSortFlow() {
    const shopAllGrid = document.getElementById('product-grid');
    return (
      shopAllGrid?.dataset.ciwEyeglassesShopAllSort === 'true' &&
      !FacetFiltersForm.isNativeEyeglassesSort()
    );
  }

  static getCardSortPrice(card) {
    const fromData = parseInt(card.dataset.ciwSortPrice || '', 10);
    if (Number.isFinite(fromData) && fromData > 0) return fromData;

    const priceEl = card.querySelector('.ciw-card__price');
    if (!priceEl) return 0;

    const matches = String(priceEl.textContent || '').match(/\d[\d,]*(?:\.\d{2})?/g) || [];
    const cents = matches
      .map((match) => {
        const normalized = match.replace(/,/g, '');
        if (normalized.indexOf('.') !== -1) {
          return Math.round(parseFloat(normalized) * 100);
        }
        return parseInt(normalized, 10) * 100;
      })
      .filter((value) => Number.isFinite(value) && value > 0);

    return cents.length ? Math.min(...cents) : 0;
  }

  static getCardSortTitle(card) {
    const fromData = String(card.dataset.ciwSortTitle || '').trim();
    if (fromData) return fromData.toLowerCase();

    const titleEl = card.querySelector('.ciw-card__title');
    return String(titleEl ? titleEl.textContent : '')
      .trim()
      .toLowerCase();
  }

  static applyNativeEyeglassesSort(grid) {
    if (!grid || !FacetFiltersForm.isNativeEyeglassesSort()) return;

    const sort = new URLSearchParams(window.location.search).get('sort_by');
    const cards = Array.from(grid.querySelectorAll('.ciw-card'));
    if (cards.length < 2) return;

    cards.forEach((card) => {
      delete card.dataset.ciwSettled;
    });

    cards.sort((a, b) => {
      if (sort === 'price-ascending' || sort === 'price-descending') {
        const priceA = FacetFiltersForm.getCardSortPrice(a);
        const priceB = FacetFiltersForm.getCardSortPrice(b);
        if (priceA !== priceB) {
          return sort === 'price-ascending' ? priceA - priceB : priceB - priceA;
        }
      } else {
        const titleA = FacetFiltersForm.getCardSortTitle(a);
        const titleB = FacetFiltersForm.getCardSortTitle(b);
        if (titleA !== titleB) {
          const cmp = titleA.localeCompare(titleB);
          return sort === 'title-descending' ? -cmp : cmp;
        }
      }

      return (a.dataset.handle || '').localeCompare(b.dataset.handle || '');
    });

    cards.forEach((card) => {
      grid.appendChild(card);
    });
  }

  static isContactsPlpPage() {
    if (FacetFiltersForm.getCollectionHandle() === 'contacts') return true;
    return Boolean(document.querySelector('[data-ciw-contacts-brand-filter]'));
  }

  static getContactsFacetFetchParams(searchParams) {
    const params = new URLSearchParams(searchParams || '');
    params.delete('filter.v.availability');
    params.delete('preview_theme_id');
    params.delete('section_id');
    params.delete('page');
    return params;
  }

  static shouldSkipContactsFacetFetch(searchParams, event) {
    if (!FacetFiltersForm.isContactsPlpPage()) return false;
    if (
      event?.target?.closest?.('[data-ciw-contacts-brand-input], [data-ciw-contacts-lens-type-input]')
    ) {
      return true;
    }
    return FacetFiltersForm.getContactsFacetFetchParams(searchParams).toString() === '';
  }

  static getCollectionHandle() {
    const match = window.location.pathname.match(/\/collections\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  static async getInStockVendors(collectionHandle) {
    const cacheKey = `ciw:in-stock-vendors:${collectionHandle}`;
    const ttlMs = 30 * 60 * 1000;

    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const { at, vendors } = JSON.parse(cached);
        if (Date.now() - at < ttlMs) return new Set(vendors);
      }
    } catch (e) {
      /* ignore cache errors */
    }

    const vendors = new Set();
    let page = 1;
    const maxPages = 3;

    while (page <= maxPages) {
      const response = await fetch(
        `/collections/${encodeURIComponent(collectionHandle)}/products.json?limit=250&page=${page}`
      );
      if (!response.ok) break;

      const data = await response.json();
      const products = data.products || [];
      if (!products.length) break;

      products.forEach((product) => {
        const variants = product.variants || [];
        if (variants.some((variant) => variant.available)) {
          vendors.add(product.vendor);
        }
      });

      page += 1;
    }

    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), vendors: [...vendors] }));
    } catch (e) {
      /* ignore cache errors */
    }

    return vendors;
  }

  static vendorFilterGeneration = 0;

  static applyVendorInStockFilter() {
    if (!FacetFiltersForm.isCiwCollection()) return;

    const collectionHandle = FacetFiltersForm.getCollectionHandle();
    if (!collectionHandle) return;

    const generation = ++FacetFiltersForm.vendorFilterGeneration;

    FacetFiltersForm.getInStockVendors(collectionHandle).then((inStockVendors) => {
      if (generation !== FacetFiltersForm.vendorFilterGeneration) return;

      document.querySelectorAll('input[name="filter.p.vendor"]').forEach((input) => {
        const vendor = input.value;
        if (!vendor || vendor.startsWith('gid://')) return;

        const item = input.closest('.facets__item, .mobile-facets__item');
        if (!item) return;

        const show = inStockVendors.has(vendor) || input.checked;
        item.hidden = !show;
      });
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static shouldPreferFullPageCollectionNavigation() {
    if (!window.matchMedia('(max-width: 749px)').matches) return false;
    if (!FacetFiltersForm.isCiwCollection()) return false;
    if (FacetFiltersForm.isContactsPlpPage()) return false;
    return true;
  }

  static shouldFullPageNativeEyeglassesSort(searchParams) {
    if (!FacetFiltersForm.isCiwCollection() || FacetFiltersForm.isContactsPlpPage()) return false;

    const grid = document.getElementById('product-grid');
    if (!grid || grid.dataset.ciwEyeglassesPlp !== 'true') return false;

    const params = new URLSearchParams(searchParams || FacetFiltersForm.activeSearchParams || '');
    const sort = params.get('sort_by');
    return (
      sort === 'price-ascending' ||
      sort === 'price-descending' ||
      sort === 'title-ascending' ||
      sort === 'title-descending'
    );
  }

  /* Shape filters must full-reload: Shopify keeps only one shape= value, and shop-all AJAX can leave the old grid. */
  static getShapeFilterSignature(searchParams) {
    const params = searchParams instanceof URLSearchParams
      ? searchParams
      : new URLSearchParams(searchParams || '');
    const parts = [];
    params.forEach((value, key) => {
      if (!String(value || '').trim()) return;
      const normalizedKey = key.toLowerCase();
      if (normalizedKey.indexOf('shape') !== -1 || normalizedKey === 'ciw_shape_group') {
        parts.push(normalizedKey + '=' + String(value).trim());
      }
    });
    return parts.sort().join('&');
  }

  static shouldFullPageEyeglassesShapeFilter(searchParams) {
    if (!FacetFiltersForm.isCiwCollection() || FacetFiltersForm.isContactsPlpPage()) return false;

    const grid = document.getElementById('product-grid');
    if (!grid || grid.dataset.ciwEyeglassesPlp !== 'true') return false;

    const prev = FacetFiltersForm.getShapeFilterSignature(
      FacetFiltersForm.searchParamsPrev || window.location.search.slice(1)
    );
    const next = FacetFiltersForm.getShapeFilterSignature(searchParams);
    return prev !== next;
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    if (typeof window.ciwPreservePopularBrandsGlassesTypeParams === 'function') {
      searchParams = window.ciwPreservePopularBrandsGlassesTypeParams(searchParams);
    }
    searchParams = FacetFiltersForm.ensureInStockAvailabilityParams(searchParams);
    searchParams = ciwAppendPreviewThemeToParams(new URLSearchParams(searchParams)).toString();

    if (FacetFiltersForm.shouldPreferFullPageCollectionNavigation()) {
      const nextUrl = `${window.location.pathname}?${searchParams}`;
      const currentUrl = `${window.location.pathname}${window.location.search}`;
      if (currentUrl !== nextUrl) {
        window.location.assign(nextUrl);
      }
      return;
    }

    if (FacetFiltersForm.shouldSkipContactsFacetFetch(searchParams, event)) {
      FacetFiltersForm.clearCollectionLoadingState();
      return;
    }

    if (FacetFiltersForm.shouldFullPageNativeEyeglassesSort(searchParams)) {
      if (typeof window.ciwShowNativeSortPageOverlay === 'function') {
        window.ciwShowNativeSortPageOverlay();
      }
      window.location.assign(`${window.location.pathname}?${searchParams}`);
      return;
    }

    if (FacetFiltersForm.shouldFullPageEyeglassesShapeFilter(searchParams)) {
      window.location.assign(`${window.location.pathname}?${searchParams}`);
      return;
    }

    const prevParams = new URLSearchParams(FacetFiltersForm.searchParamsPrev || '');
    const nextParams = new URLSearchParams(searchParams || '');
    if (prevParams.get('sort_by') !== nextParams.get('sort_by')) {
      FacetFiltersForm.filterData = [];
    }

    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.activeSearchParams = searchParams;
    FacetFiltersForm.fetchGeneration += 1;

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    if (FacetFiltersForm.isCiwCollection()) {
      FacetFiltersForm.setProductGridLoading(true);
    } else {
      const loadingSpinners = document.querySelectorAll(
        '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
      );
      loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
      if (countContainer) {
        countContainer.classList.add('loading');
      }
      if (countContainerDesktop) {
        countContainerDesktop.classList.add('loading');
      }
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });
  }

  static parseSectionHtml(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  static getProductGridStats(parsedHtml) {
    const grid = parsedHtml.getElementById('product-grid');
    if (!grid) return { cardCount: 0, hasEmptyNote: false };
    return {
      cardCount: grid.querySelectorAll('.ciw-card').length,
      hasEmptyNote: Boolean(grid.querySelector('.ciw-plp-grid-empty-note, .collection--empty')),
    };
  }

  static isAvailabilityOnlyParams(searchParams) {
    const params = new URLSearchParams(searchParams || '');
    params.delete('filter.v.availability');
    params.delete('sort_by');
    params.delete('page');
    params.delete('section_id');
    params.delete('preview_theme_id');
    return !params.toString();
  }

  static getExpectedCollectionProductCount() {
    const totalEl = document.querySelector('.ciw-view-more-wrap[data-total]');
    if (totalEl) {
      const n = parseInt(totalEl.dataset.total, 10);
      if (Number.isFinite(n) && n > 0) return n;
    }

    const countText = document.querySelector('.ciw-coll-title__count')?.textContent || '';
    const match = countText.match(/(\d[\d,]*)/);
    if (match) {
      const n = parseInt(match[1].replace(/,/g, ''), 10);
      if (Number.isFinite(n) && n > 0) return n;
    }

    const currentGrid = document.getElementById('product-grid');
    return currentGrid ? currentGrid.querySelectorAll('.ciw-card').length : 0;
  }

  static shouldKeepExistingGridOnEmptyFetch(parsedHtml) {
    if (!FacetFiltersForm.isCiwCollection()) return false;

    const { cardCount: fetchedCards, hasEmptyNote } = FacetFiltersForm.getProductGridStats(parsedHtml);
    if (fetchedCards > 0 || !hasEmptyNote) return false;

    const currentGrid = document.getElementById('product-grid');
    const currentCards = currentGrid ? currentGrid.querySelectorAll('.ciw-card').length : 0;
    if (currentCards === 0) return false;
    if (!FacetFiltersForm.isAvailabilityOnlyParams(FacetFiltersForm.searchParamsPrev)) return false;

    return FacetFiltersForm.getExpectedCollectionProductCount() > 0;
  }

  static shouldKeepExistingGridOnSparseFetch(parsedHtml) {
    if (!FacetFiltersForm.isCiwCollection() || FacetFiltersForm.isContactsPlpPage()) return false;
    if (!window.matchMedia('(max-width: 749px)').matches) return false;

    const { cardCount: fetchedCards } = FacetFiltersForm.getProductGridStats(parsedHtml);
    const currentGrid = document.getElementById('product-grid');
    const currentCards = currentGrid ? currentGrid.querySelectorAll('.ciw-card').length : 0;

    return currentCards > 0 && fetchedCards > 0 && fetchedCards < currentCards;
  }

  static shouldRetryEmptySectionFetch(parsedHtml, attempt) {
    if (attempt >= 4) return false;

    const { cardCount, hasEmptyNote } = FacetFiltersForm.getProductGridStats(parsedHtml);
    if (cardCount > 0 || !hasEmptyNote) return false;

    return FacetFiltersForm.isAvailabilityOnlyParams(FacetFiltersForm.searchParamsPrev);
  }

  static retryEmptySectionFetch(url, event, attempt) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(FacetFiltersForm.renderSectionFromFetch(url, event, attempt));
      }, 150 * attempt);
    });
  }

  static renderSectionFromFetch(url, event, attempt = 0) {
    const generation = FacetFiltersForm.fetchGeneration;
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Facet fetch failed');
        return response.text();
      })
      .then((responseText) => {
        if (generation !== FacetFiltersForm.fetchGeneration) return;

        const parsedHtml = FacetFiltersForm.parseSectionHtml(responseText);
        if (FacetFiltersForm.shouldRetryEmptySectionFetch(parsedHtml, attempt)) {
          return FacetFiltersForm.retryEmptySectionFetch(url, event, attempt + 1);
        }

        if (FacetFiltersForm.shouldKeepExistingGridOnEmptyFetch(parsedHtml)) {
          FacetFiltersForm.clearCollectionLoadingState();
          document.dispatchEvent(new CustomEvent('ciw:collection-updated'));
          return;
        }

        if (FacetFiltersForm.shouldKeepExistingGridOnSparseFetch(parsedHtml)) {
          FacetFiltersForm.clearCollectionLoadingState();
          document.dispatchEvent(new CustomEvent('ciw:collection-updated'));
          return;
        }

        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html: responseText, url }];
        FacetFiltersForm.renderFilters(responseText, event);
        FacetFiltersForm.renderProductGridContainer(responseText, parsedHtml);
        FacetFiltersForm.renderProductCount(responseText);
        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(parsedHtml);
        }
      })
      .catch(() => {
        if (generation !== FacetFiltersForm.fetchGeneration) return;
        FacetFiltersForm.clearCollectionLoadingState();
        const loadingSpinners = document.querySelectorAll(
          '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
        );
        loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
        const countContainer = document.getElementById('ProductCount');
        const countContainerDesktop = document.getElementById('ProductCountDesktop');
        if (countContainer) countContainer.classList.remove('loading');
        if (countContainerDesktop) countContainerDesktop.classList.remove('loading');
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    const parsedHtml = FacetFiltersForm.parseSectionHtml(html);

    if (FacetFiltersForm.shouldKeepExistingGridOnEmptyFetch(parsedHtml)) {
      FacetFiltersForm.clearCollectionLoadingState();
      document.dispatchEvent(new CustomEvent('ciw:collection-updated'));
      return;
    }

    if (FacetFiltersForm.shouldKeepExistingGridOnSparseFetch(parsedHtml)) {
      FacetFiltersForm.clearCollectionLoadingState();
      document.dispatchEvent(new CustomEvent('ciw:collection-updated'));
      return;
    }

    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html, parsedHtml);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(parsedHtml);
    }
  }

  static renderProductGridContainer(html, parsedHtml = null) {
    const parsed = parsedHtml || FacetFiltersForm.parseSectionHtml(html);
    const fetchedContainer = parsed.getElementById('ProductGridContainer');
    const targetContainer = document.getElementById('ProductGridContainer');

    if (!fetchedContainer || !targetContainer) {
      FacetFiltersForm.clearCollectionLoadingState();
      return;
    }

    targetContainer.innerHTML = fetchedContainer.innerHTML;

    const shopAllGrid = document.getElementById('product-grid');
    if (FacetFiltersForm.shouldRunShopAllSortFlow()) {
      const wrap = document.querySelector('.ciw-product-grid-wrap--shop-all-sort');
      if (wrap) wrap.classList.add('is-loading');
      if (typeof window.ciwShopAllPrepareGrid === 'function') {
        window.ciwShopAllPrepareGrid();
      } else if (typeof window.ciwEyeglassesShopAllSortRun === 'function') {
        window.ciwEyeglassesShopAllSortRun();
      }
    } else {
      FacetFiltersForm.clearCollectionLoadingState();
      if (FacetFiltersForm.isNativeEyeglassesSort()) {
        FacetFiltersForm.applyNativeEyeglassesSort(document.getElementById('product-grid'));
        if (typeof window.ciwRevealNativeSortGridNow === 'function') {
          window.ciwRevealNativeSortGridNow();
        }
      }
    }

    targetContainer.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    document.dispatchEvent(new CustomEvent('ciw:collection-updated'));

    if (FacetFiltersForm.isNativeEyeglassesSort()) {
      requestAnimationFrame(() => {
        FacetFiltersForm.applyNativeEyeglassesSort(document.getElementById('product-grid'));
        FacetFiltersForm.clearCollectionLoadingState();
      });
    }
  }

  static setProductGridLoading(loading) {
    if (!FacetFiltersForm.isCiwCollection()) return;
    if (FacetFiltersForm.isContactsPlpPage()) return;

    const gridWrap = document.querySelector('.ciw-product-grid-wrap');
    if (!gridWrap) return;

    if (loading) {
      gridWrap.classList.add('is-loading');
      return;
    }

    if (
      gridWrap.classList.contains('ciw-product-grid-wrap--shop-all-sort') &&
      !FacetFiltersForm.isNativeEyeglassesSort()
    ) {
      return;
    }

    FacetFiltersForm.clearCollectionLoadingState();
  }

  static clearCollectionLoadingState() {
    const productGrid = document.getElementById('product-grid');
    if (
      productGrid?.dataset.ciwEyeglassesShopAllSort === 'true' &&
      !FacetFiltersForm.isNativeEyeglassesSort()
    ) {
      return;
    }
    if (productGrid) {
      productGrid.style.removeProperty('min-height');
    }
    const gridWrap = document.querySelector('.ciw-product-grid-wrap');
    if (gridWrap) {
      gridWrap.classList.remove('is-loading');
    }
    const skeleton = document.querySelector('[data-ciw-plp-skeleton]');
    if (skeleton) {
      skeleton.setAttribute('aria-busy', 'false');
    }
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const countEl = parsedHTML.getElementById('ProductCount');
    const count = countEl ? countEl.innerHTML : '';
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const ciwCount = document.getElementById('CiwProductCount');
    const ciwCountFromHtml = parsedHTML.getElementById('CiwProductCount');
    const ciwCountMobile =
      document.getElementById('CiwProductCountMobile') ||
      document.querySelector('.ciw-grid-toolbar__count--mobile');
    const ciwCountMobileFromHtml =
      parsedHTML.getElementById('CiwProductCountMobile') ||
      parsedHTML.querySelector('.ciw-grid-toolbar__count--mobile');
    const ciwCountText =
      ciwCountMobileFromHtml?.innerHTML || ciwCountFromHtml?.innerHTML || count;
    if (ciwCount) {
      ciwCount.innerHTML = ciwCountText;
    }
    if (ciwCountMobile && ciwCountText) {
      ciwCountMobile.innerHTML = ciwCountText;
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderToolbarPills(parsedHTML);
    FacetFiltersForm.renderMobileToolbarFilters(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }

    FacetFiltersForm.applyVendorInStockFilter();
  }

  static renderToolbarPills(html) {
    const sourcePills = html.querySelector('.ciw-grid-toolbar__pills');
    const targetPills = document.querySelector('.ciw-grid-toolbar__pills');
    if (sourcePills && targetPills) {
      targetPills.innerHTML = sourcePills.innerHTML;
    }
  }

  static renderMobileToolbarFilters(html) {
    const source = html.querySelector('.ciw-grid-toolbar__mobile-filters');
    const target = document.querySelector('.ciw-grid-toolbar__mobile-filters');
    if (!source || !target) return;
    target.innerHTML = source.innerHTML;
    document.dispatchEvent(new CustomEvent('ciw:collection-updated'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    const mobileFacetForm = document.getElementById('FacetFiltersFormMobile');
    const mobileFacetDrawer = mobileFacetForm?.closest('menu-drawer');
    if (mobileFacetDrawer && typeof mobileFacetDrawer.bindEvents === 'function') {
      mobileFacetDrawer.bindEvents();
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const detailsElement = target.closest('details');
    if (detailsElement?.closest('.ciw-mobile-vertical-filters')) {
      const countBadge = detailsElement.querySelector('.ciw-facet-count-badge');
      detailsElement.classList.toggle(
        'ciw-facets__disclosure--active',
        Boolean(countBadge && !countBadge.classList.contains('hidden'))
      );
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
      return;
    }

    if (target.classList.contains('facets__disclosure-vertical')) {
      FacetFiltersForm.renderCounts(source, target);
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  static ensureInStockAvailabilityParams(searchParams) {
    if (!document.body.classList.contains('template-collection')) return searchParams;
    if (!FacetFiltersForm.isCiwCollection()) return searchParams;

    const params = new URLSearchParams(searchParams || '');
    if (!params.has('filter.v.availability')) {
      params.set('filter.v.availability', '1');
    }
    return params.toString();
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams(formData);
    return FacetFiltersForm.ensureInStockAvailabilityParams(params.toString());
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.activeSearchParams = '';
FacetFiltersForm.fetchGeneration = 0;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

function ciwAppendPreviewThemeToParams(params) {
  const grid = document.getElementById('product-grid');
  let previewId = new URLSearchParams(window.location.search).get('preview_theme_id') || '';
  if (!previewId) {
    try {
      previewId = sessionStorage.getItem('ciw_preview_theme_id') || '';
    } catch (e) {
      /* ignore */
    }
  }
  if (!previewId) {
    previewId = grid?.dataset.ciwPreviewThemeId || '';
  }
  if (previewId && !params.has('preview_theme_id')) {
    params.set('preview_theme_id', previewId);
  }
  return params;
}

function ciwAvailabilityCollectionUrl(params) {
  return `${window.location.pathname}?${ciwAppendPreviewThemeToParams(params).toString()}`;
}

function ciwApplyDefaultInStockCollectionFilters() {
  if (!document.body.classList.contains('template-collection')) return;
  if (!FacetFiltersForm.isCiwCollection()) return;
  if (FacetFiltersForm.isContactsPlpPage()) return;

  const params = new URLSearchParams(window.location.search);
  if (params.has('filter.v.availability')) return;

  const grid = document.getElementById('product-grid');
  const isShopAllSort = grid && grid.dataset.ciwEyeglassesShopAllSort === 'true';

  params.set('filter.v.availability', '1');
  const nextParams = ciwAppendPreviewThemeToParams(params).toString();
  const isMobile = window.matchMedia('(max-width: 749px)').matches;

  if (isShopAllSort) {
    window.history.replaceState({}, '', ciwAvailabilityCollectionUrl(params));
    FacetFiltersForm.renderPage(nextParams, null, true);
    return;
  }

  if (isMobile) {
    try {
      sessionStorage.removeItem('ciw:plp-empty-reload');
    } catch (e) {
      /* ignore */
    }
    window.location.replace(ciwAvailabilityCollectionUrl(params));
    return;
  }

  FacetFiltersForm.renderPage(nextParams, null, true);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', ciwApplyDefaultInStockCollectionFilters);
} else {
  ciwApplyDefaultInStockCollectionFilters();
}

document.addEventListener('DOMContentLoaded', () => {
  const runVendorFilter = () => FacetFiltersForm.applyVendorInStockFilter();
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(runVendorFilter, { timeout: 4000 });
  } else {
    window.setTimeout(runVendorFilter, 150);
  }
});

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
